import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import ShareDialog from "./components/ShareDialog";
import { getMovieById, getRelatedMovies } from "@/utils/content";

export default function Detail() {
  const { id } = useParams();
  const movie = getMovieById(id);
  const [shareOpen, setShareOpen] = useState(false);

  if (!movie) {
    return (
      <div className="w-full px-4 md:px-8 py-16 flex flex-col items-center">
        <i className="ri-emotion-sad-line text-5xl text-background-400"></i>
        <h1 className="mt-4 text-xl font-heading font-semibold text-foreground-950">内容未找到</h1>
        <p className="mt-2 text-sm text-foreground-600">该影视作品不存在或已下架。</p>
        <Link
          to="/"
          className="mt-6 px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-label font-semibold hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const related = getRelatedMovies(movie, 4);
  const infoRows = [
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.cast.join(" / ") },
    { label: "类型", value: movie.genres.join(" · ") },
    { label: "地区", value: movie.region },
    { label: "上映时间", value: `${movie.year}年` },
    { label: "片长", value: movie.duration },
  ];

  return (
    <div className="w-full">
      {/* Banner */}
      <section className="relative w-full bg-primary-950 overflow-hidden">
        <img
          src={movie.cover}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-top opacity-25 blur-sm"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/85 to-primary-950/70"></div>
        <div className="relative z-10 px-4 md:px-8 pt-6 pb-8 md:pb-10">
          <div className="[&_a]:text-background-300 [&_span]:text-background-200">
            <Breadcrumb items={[{ label: movie.type === "movie" ? "电影" : "电视剧", to: `/category/${movie.type}` }, { label: movie.title }]} />
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-6">
            <div className="w-40 md:w-52 shrink-0 rounded-lg overflow-hidden bg-primary-900 aspect-[2/3]">
              <img
                src={movie.cover}
                alt={`${movie.title} 海报`}
                title={`${movie.title} - 欲望之岛`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2 py-0.5 rounded bg-accent-500 text-primary-950 text-xs font-label font-semibold">
                  {movie.type === "movie" ? "电影" : "电视剧"}
                </span>
                {movie.tags.map((t) => (
                  <span
                    key={t}
                    className="px-2 py-0.5 rounded bg-secondary-500/90 text-primary-950 text-xs font-label"
                  >
                    {t}
                  </span>
                ))}
              </div>
              <h1 className="mt-3 text-2xl md:text-4xl font-heading font-bold text-background-50">
                {movie.title}
              </h1>
              <p className="mt-2 text-sm md:text-base text-background-300 font-label">
                {movie.genres.join(" · ")} · {movie.year} · {movie.region}
              </p>
              <div className="mt-3 flex items-center gap-3">
                <span className="text-3xl font-heading font-bold text-accent-400">
                  {movie.rating.toFixed(1)}
                </span>
                <span className="text-xs text-background-400">综合评分</span>
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <Link
                  to={`/category/${movie.type}`}
                  className="px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-label font-semibold hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
                >
                  相似作品
                </Link>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="flex items-center gap-1.5 px-5 py-2.5 rounded-md border border-background-50/30 text-background-50 text-sm font-label hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-share-forward-line"></i>
                  分享
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="rounded-lg bg-background-50 border border-background-200 p-6">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 flex items-center gap-2">
                <span className="w-1.5 h-5 rounded-full bg-accent-500"></span>
                剧情简介
              </h2>
              <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                {movie.synopsis}
              </p>
              <p className="mt-3 text-sm md:text-base text-foreground-700 leading-relaxed">
                《{movie.title}》是一部{movie.region}出品的{movie.genres.join("、")}作品，由{movie.director}
                执导，{movie.cast.slice(0, 3).join("、")}等主演。影片以细腻的叙事与扎实的制作，为观众呈现了一段值得回味的观影体验。
              </p>
            </div>

            <div className="mt-6 rounded-lg bg-background-50 border border-background-200 p-6">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 flex items-center gap-2">
                <span className="w-1.5 h-5 rounded-full bg-accent-500"></span>
                影视信息
              </h2>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                {infoRows.map((row) => (
                  <div
                    key={row.label}
                    className="flex gap-4 py-3 border-b border-background-200 last:border-b-0"
                  >
                    <span className="w-16 shrink-0 text-sm text-foreground-500 font-label">
                      {row.label}
                    </span>
                    <span className="text-sm text-foreground-800">{row.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <aside>
            <div className="rounded-lg bg-background-50 border border-background-200 p-5">
              <h2 className="text-lg font-heading font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-magic-line text-accent-500"></i>
                相关推荐
              </h2>
              <div className="mt-4 grid grid-cols-2 gap-3">
                {related.map((m) => (
                  <MovieCard key={m.id} movie={m} />
                ))}
              </div>
            </div>

            <div className="mt-6 rounded-lg bg-primary-950 p-5 text-center">
              <i className="ri-heart-3-line text-3xl text-accent-400"></i>
              <p className="mt-3 text-sm text-background-200">
                喜欢「{movie.title}」？把本站分享给同样爱追剧的朋友吧
              </p>
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="mt-4 px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-label font-semibold hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
              >
                立即分享
              </button>
            </div>
          </aside>
        </div>
      </div>

      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} title={movie.title} />
    </div>
  );
}